import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../utils/api';
import {
  FolderKanban, Users, Clock, TrendingUp, ArrowRight, CalendarDays, Bell,
  FileText, Wallet, Package, ChevronRight, AlertTriangle, FileStack,
  Wrench, Settings2, X, GripVertical, Eye, EyeOff, Sparkles, HardHat, Calculator, Plus
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import AICalcPickerModal from '../../components/AICalcPickerModal';
import QuickTotalCalcModal from '../../components/QuickTotalCalcModal';
import DailyBriefingWidget from '../../components/DailyBriefingWidget';
import RevenueChartWidget from '../../components/RevenueChartWidget';
import HealthScoreWidget from '../../components/HealthScoreWidget';
import FeatureTip from '../../components/FeatureTip';
import { HERO_IMAGE } from '../../lib/projectVisuals';
import { useTheme } from '../../lib/theme';
import { Sun, Moon } from 'lucide-react';

const WIDGET_META = {
  stats: { label: 'Statistikkort', icon: TrendingUp },
  quick_links: { label: 'Snabblänkar', icon: ChevronRight },
  active_projects: { label: 'Aktiva projekt', icon: FolderKanban },
  reminders: { label: 'Påminnelser', icon: Bell },
  unpaid_invoices: { label: 'Obetalda fakturor', icon: FileStack },
  eta_overview: { label: 'ÄTA-översikt', icon: FileText },
  time_report_status: { label: 'Tidrapporter', icon: Clock },
  machine_hours: { label: 'Maskintimmar', icon: Wrench },
};

const Dashboard = () => {
  const [theme, toggleTheme] = useTheme();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyVerified, setCompanyVerified] = useState(true);
  const [widgets, setWidgets] = useState({});
  const [showWidgetPanel, setShowWidgetPanel] = useState(false);
  const [aiCalcKind, setAiCalcKind] = useState(null);
  const [showTotalCalc, setShowTotalCalc] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, projectsRes, remindersRes, verRes, widgetRes] = await Promise.all([
        api.getDashboardStats(),
        api.getProjects(),
        api.getReminders({ is_completed: false }),
        api.getCompanyVerificationStatus().catch(() => ({ data: { is_verified: true } })),
        api.getWidgetPreferences().catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data);
      setProjects(projectsRes.data.filter(p => p.status === 'active').slice(0, 5));
      setReminders((remindersRes.data || []).slice(0, 5));
      setCompanyVerified(verRes.data.is_verified);
      setWidgets(widgetRes.data || {});
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const toggleWidget = async (key) => {
    const updated = { ...widgets, [key]: !widgets[key] };
    setWidgets(updated);
    try { await api.saveWidgetPreferences(updated); }
    catch { toast.error('Kunde inte spara'); }
  };

  const w = (key) => widgets[key] !== false; // default true unless explicitly false

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
      <div className="w-6 h-6 border-2 border-[#E8613A] border-t-transparent op-round rounded-full animate-spin" />
    </div>
  );

  const statCards = [
    { label: 'AKTIVA PROJEKT', value: stats?.active_projects || 0, icon: FolderKanban, link: '/dashboard/projects' },
    { label: 'ANSTÄLLDA', value: stats?.total_employees || 0, icon: Users, link: '/dashboard/employees' },
    { label: 'VÄNTANDE RAPPORTER', value: stats?.pending_time_reports || 0, icon: Clock, link: '/dashboard/time-reports' },
    { label: 'UPPHANDLINGAR', value: stats?.tenders_without_analysis || 0, icon: TrendingUp, link: '/dashboard/tenders' },
  ];

  const quickLinks = [
    { label: 'ÄTA-rapporter', icon: FileText, link: '/dashboard/eta' },
    { label: 'Löner', icon: Wallet, link: '/dashboard/payroll' },
    { label: 'Leverantörer', icon: Package, link: '/dashboard/suppliers' },
    { label: 'Kalender', icon: CalendarDays, link: '/dashboard/calendar' },
  ];

  const isOverdue = (dateStr) => new Date(dateStr) < new Date(new Date().setHours(0, 0, 0, 0));
  const isToday = (dateStr) => dateStr === new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="dashboard-page">
      <div className="relative overflow-hidden rounded-3xl bg-[#121110] text-white min-h-[260px] flex items-end op-rise" data-testid="dashboard-hero">
        <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#121110] via-[#121110]/80 to-transparent" />
        <button onClick={toggleTheme} data-testid="theme-toggle-btn" aria-label="Byt tema"
          className="absolute top-4 right-4 z-10 h-10 pl-1 pr-3 rounded-full bg-white/10 backdrop-blur border border-white/15 text-white text-xs font-semibold inline-flex items-center gap-2 hover:bg-white/20 transition-colors">
          <span className="w-8 h-8 rounded-full bg-[#F26B1F] flex items-center justify-center">{theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}</span>
          {theme === 'dark' ? 'Ljust läge' : 'Mörkt läge'}
        </button>
        <div className="relative p-6 sm:p-9 max-w-xl">
          <p className="flex items-center gap-2 text-sm font-semibold text-white/70"><img src="/operio-logo-2.png" alt="" className="w-5 h-5" />Operio AI</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-bold leading-[1.05]">Från ritning<br />till <span className="text-[#F26B1F]">färdig kalkyl.</span></h1>
          <p className="mt-3 text-sm text-white/70 max-w-md">AI-driven material-, arbets- och tidsberäkning som sparar tid, minskar spill och ger dig full kontroll.</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link to="/dashboard/projects?new=1" data-testid="dashboard-create-project-btn" className="op-btn-primary h-12 px-6"><Plus size={16} />Starta ny beräkning</Link>
            <Link to="/dashboard/projects" data-testid="dashboard-my-projects-link" className="h-12 px-5 rounded-xl border border-white/25 text-sm font-semibold inline-flex items-center hover:bg-white/10 transition-colors">Mina projekt</Link>
            <button onClick={() => setShowWidgetPanel(!showWidgetPanel)} data-testid="toggle-widget-panel" className="h-12 px-4 rounded-xl text-sm text-white/70 hover:text-white inline-flex items-center gap-1.5"><Settings2 size={15} />Anpassa</button>
          </div>
        </div>
      </div>

      {/* Widget configuration panel */}
      {showWidgetPanel && (
        <div className="bg-card border border-border rounded-xl p-4" data-testid="widget-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-foreground">Visa/dölj widgets</h3>
            <button onClick={() => setShowWidgetPanel(false)} className="text-muted-foreground hover:text-foreground">
              <X size={16} />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Object.entries(WIDGET_META).map(([key, meta]) => {
              const active = w(key);
              const Icon = meta.icon;
              return (
                <button key={key} onClick={() => toggleWidget(key)}
                  data-testid={`widget-toggle-${key}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all ${
                    active ? 'border-[#E8613A]/30 bg-[#E8613A]/5 text-foreground' : 'border-border bg-muted/30 text-muted-foreground line-through'
                  }`}>
                  {active ? <Eye size={12} className="text-[#E8613A]" /> : <EyeOff size={12} />}
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Quick-Calc Buttons */}
      <FeatureTip tipId="dashboard-ai-buttons" title="Snabb AI-beräkning" body="Klicka på en av de orange knapparna för att direkt starta en AI-beräkning. Välj projekt eller skapa ett nytt — resten sköter jag. Klicka på 'Totalberäkning' för en snabb beräkning utan projekt." position="top-right" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="ai-quick-calc-row">
        {[
          { kind: 'material', label: 'Beräkna Material', desc: 'AI räknar ut åtgång & inköpslista', icon: Package },
          { kind: 'labor', label: 'Beräkna Arbete', desc: 'Arbetstimmar, lön & kostnad', icon: HardHat },
          { kind: 'time', label: 'Beräkna Tid', desc: 'Arbetsdagar, milstolpar & risker', icon: Clock },
        ].map((b) => {
          const Icon = b.icon;
          return (
            <button
              key={b.kind}
              onClick={() => setAiCalcKind(b.kind)}
              data-testid={`ai-calc-btn-${b.kind}`}
              className="group relative overflow-hidden rounded-2xl bg-[#1B1918] text-white p-5 text-left border border-white/5 shadow-[0_18px_40px_-24px_rgba(0,0,0,.7)] hover:-translate-y-0.5 transition-transform duration-200"
            >
              <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:bg-white/15 transition-colors" />
              <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-black/10 rounded-full" />
              <div className="relative flex items-start gap-3.5">
                <div className="w-11 h-11 bg-[#F26B1F] rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon size={20} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Sparkles size={11} className="text-white/80" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/80">AI</span>
                  </div>
                  <p className="text-base font-bold mt-0.5" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                    {b.label}
                  </p>
                  <p className="text-xs text-white/80 mt-1 leading-snug">{b.desc}</p>
                </div>
                <ArrowRight size={16} className="text-white/70 mt-2 transition-transform group-hover:translate-x-0.5" />
              </div>
            </button>
          );
        })}
      </div>

      {/* AI Total-Calc — wide button without project requirement */}
      <button
        onClick={() => setShowTotalCalc(true)}
        data-testid="ai-total-calc-btn"
        className="w-full group relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1A1A1A] to-[#2A2A2A] text-white p-5 text-left shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 border border-[#E8613A]/30"
      >
        <div className="absolute -right-12 -top-12 w-48 h-48 bg-[#E8613A]/20 rounded-full blur-3xl group-hover:bg-[#E8613A]/30 transition-colors" />
        <div className="absolute right-8 bottom-0 w-32 h-32 bg-[#E8613A]/10 rounded-full blur-2xl" />
        <div className="relative flex items-center gap-4">
          <div className="w-12 h-12 bg-[#E8613A] rounded-xl flex items-center justify-center flex-shrink-0">
            <Calculator size={22} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Sparkles size={11} className="text-[#E8613A]" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#E8613A]">Snabb AI-totalberäkning</span>
            </div>
            <p className="text-lg font-bold mt-0.5" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Räkna ut hela jobbet på en gång
            </p>
            <p className="text-xs text-white/70 mt-1 leading-snug">Material + arbete + tid — utan att skapa projekt först. Spara resultatet och gör om till projekt senare.</p>
          </div>
          <Link to="/dashboard/snabb-arkiv" onClick={(e) => e.stopPropagation()} className="hidden sm:flex text-xs font-medium text-white/70 hover:text-white items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15" data-testid="archive-link">
            Snabb-arkiv <ArrowRight size={11} />
          </Link>
          <ArrowRight size={18} className="text-white/70 group-hover:translate-x-0.5 transition-transform" />
        </div>
      </button>

      {/* AI Insights — Daily Briefing + Revenue + Health Score */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="ai-insights-row">
        <DailyBriefingWidget />
        <RevenueChartWidget />
        <HealthScoreWidget />
      </div>

      {/* Verification Banner */}
      {!companyVerified && (
        <Link to="/dashboard/settings" data-testid="verification-banner"
          className="flex items-center gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors">
          <AlertTriangle size={20} className="text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-300">Verifiera ditt företag</p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">Fyll i företagsuppgifter för att låsa upp fakturering, löner och bokföring.</p>
          </div>
          <ChevronRight size={16} className="text-amber-600" />
        </Link>
      )}

      {/* Stats Row */}
      {w('stats') && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="widget-stats">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Link key={index} to={stat.link}
                className="group bg-card border border-border rounded-xl p-5 transition-all duration-200 hover:shadow-md hover:border-[#E8613A]/30"
                data-testid={`stat-card-${index}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">{stat.label}</p>
                    <p className="text-3xl font-bold tracking-tight text-foreground mt-1">{stat.value}</p>
                  </div>
                  <div className="p-2 bg-[#E8613A]/8 rounded-lg">
                    <Icon size={16} className="text-[#E8613A]" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Quick Links */}
      {w('quick_links') && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="widget-quick-links">
          {quickLinks.map((ql, i) => {
            const Icon = ql.icon;
            return (
              <Link key={i} to={ql.link} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 hover:border-[#E8613A]/30 hover:shadow-sm transition-all group" data-testid={`quick-link-${i}`}>
                <div className="p-2 bg-[#E8613A]/8 rounded-lg"><Icon size={14} className="text-[#E8613A]" /></div>
                <span className="text-sm font-medium text-foreground">{ql.label}</span>
                <ChevronRight size={14} className="text-muted-foreground ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            );
          })}
        </div>
      )}

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Active Projects — 2 cols */}
        {w('active_projects') && (
          <div className="lg:col-span-2 bg-card border border-border rounded-xl overflow-hidden" data-testid="widget-active-projects">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="text-sm font-bold tracking-tight text-foreground flex items-center gap-2">
                <FolderKanban size={15} className="text-[#E8613A]" /> Aktiva projekt
              </h2>
              <Link to="/dashboard/projects" className="text-xs font-medium text-[#E8613A] hover:underline flex items-center gap-1">
                Visa alla <ArrowRight size={13} />
              </Link>
            </div>
            <div className="divide-y divide-border">
              {projects.length === 0 ? (
                <p className="text-sm text-muted-foreground p-5">Inga aktiva projekt</p>
              ) : projects.map((project) => (
                <Link key={project.id} to={`/dashboard/projects/${project.id}`}
                  className="flex items-center justify-between px-5 py-3.5 hover:bg-muted/40 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-8 rounded-full bg-[#E8613A]/60" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{project.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{project.customer || 'Ingen kund'}</p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{project.start_date ? new Date(project.start_date).toLocaleDateString('sv-SE') : '—'}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Reminders — 1 col */}
        {w('reminders') && (
          <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="widget-reminders">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h2 className="text-sm font-bold tracking-tight text-foreground flex items-center gap-2">
                <Bell size={15} className="text-[#E8613A]" /> Påminnelser
              </h2>
              <Link to="/dashboard/calendar" className="text-xs font-medium text-[#E8613A] hover:underline flex items-center gap-1">
                Kalender <ArrowRight size={13} />
              </Link>
            </div>
            <div className="divide-y divide-border">
              {reminders.length === 0 ? (
                <div className="p-5 text-center">
                  <CalendarDays size={24} className="text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Inga kommande påminnelser</p>
                </div>
              ) : reminders.map((r) => (
                <div key={r.id} className="px-5 py-3 flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                    isOverdue(r.due_date) ? 'bg-destructive animate-pulse' : isToday(r.due_date) ? 'bg-[#E8613A]' : 'bg-muted-foreground/30'
                  }`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{r.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(r.due_date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}
                      {r.due_time && ` kl ${r.due_time.substring(0, 5)}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Additional widget row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Unpaid Invoices */}
        {w('unpaid_invoices') && (
          <Link to="/dashboard/invoices" className="bg-card border border-border rounded-xl p-5 hover:shadow-md hover:border-[#E8613A]/30 transition-all" data-testid="widget-unpaid-invoices">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#E8613A]/8 rounded-lg"><FileStack size={16} className="text-[#E8613A]" /></div>
              <h3 className="text-sm font-bold text-foreground">Obetalda fakturor</h3>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats?.unpaid_invoices || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Totalt: {(stats?.unpaid_invoices_total || 0).toLocaleString('sv-SE')} kr
            </p>
          </Link>
        )}

        {/* ÄTA Overview */}
        {w('eta_overview') && (
          <Link to="/dashboard/eta" className="bg-card border border-border rounded-xl p-5 hover:shadow-md hover:border-[#E8613A]/30 transition-all" data-testid="widget-eta-overview">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#E8613A]/8 rounded-lg"><FileText size={16} className="text-[#E8613A]" /></div>
              <h3 className="text-sm font-bold text-foreground">Aktiva ÄTA</h3>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats?.active_eta || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Värde: {(stats?.eta_total || 0).toLocaleString('sv-SE')} kr
            </p>
          </Link>
        )}

        {/* Time Reports */}
        {w('time_report_status') && (
          <Link to="/dashboard/time-reports" className="bg-card border border-border rounded-xl p-5 hover:shadow-md hover:border-[#E8613A]/30 transition-all" data-testid="widget-time-reports">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#E8613A]/8 rounded-lg"><Clock size={16} className="text-[#E8613A]" /></div>
              <h3 className="text-sm font-bold text-foreground">Väntande tidrapporter</h3>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats?.pending_time_reports || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Behöver godkännas</p>
          </Link>
        )}

        {/* Machine Hours */}
        {w('machine_hours') && (
          <Link to="/dashboard/machine-hours" className="bg-card border border-border rounded-xl p-5 hover:shadow-md hover:border-[#E8613A]/30 transition-all" data-testid="widget-machine-hours">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 bg-[#E8613A]/8 rounded-lg"><Wrench size={16} className="text-[#E8613A]" /></div>
              <h3 className="text-sm font-bold text-foreground">Maskintimmar idag</h3>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats?.today_machine_hours || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">Registrerade idag</p>
          </Link>
        )}
      </div>

      {aiCalcKind && (
        <AICalcPickerModal
          kind={aiCalcKind}
          onClose={() => setAiCalcKind(null)}
          onWithoutProject={() => { setAiCalcKind(null); setShowTotalCalc(true); }}
        />
      )}
      {showTotalCalc && <QuickTotalCalcModal onClose={() => setShowTotalCalc(false)} />}
    </div>
  );
};

export default Dashboard;
